import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { base64ToObject, removeBlank } from '@services/core-services/utils';
import { environment } from '../../../environments/environment';
import { AppService } from '@services/core-services/app.service';
import { TokenService } from '@services/auth-services/token.service';
import { Query } from '@services/core-services/models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(
    protected readonly http: HttpClient,
    protected readonly tokenService: TokenService,
    protected readonly appService: AppService
  ) {}

  //TODO: public get<T>(path: string, query?: any): Observable<T> {
  //   const params = this.prepareQuery(query);
  //   const url = this.combine([this.getBaseUrl(), path]);
  //   const headers = this.withHeaders();
  //   return this.http.get<T>(url, { params, headers });
  // }

  public get<T>(path: string, query?: any, headers: any = null): Observable<T> {
    const params = this.prepareQuery(query);
    const url = this.combine([this.getBaseUrl(), path]);
    if (!headers) {
      headers = this.withHeaders();
    }
    return this.http.get<T>(url, { params, headers });
  }

  public post<M, T>(path: string, payload: M, query?: any): Observable<T> {
    const url = this.combine([this.getBaseUrl(), path]);
    const params = this.prepareQuery(query) as any as HttpParams;
    const headers = this.withHeaders();
    return this.http.post<T>(url, payload, { params, headers });
  }

  public patch<T, M>(path: string, payload: M, query?: any): Observable<T> {
    const url = this.combine([this.getBaseUrl(), path]);
    const params = this.prepareQuery(query);
    const headers = this.withHeaders();
    return this.http.patch<T>(url, payload, { params, headers });
  }

  public put<T, M>(path: string, payload: T, query?: any): Observable<M> {
    const url = this.combine([this.getBaseUrl(), path]);
    const params = this.prepareQuery(query);
    const headers = this.withHeaders();
    return this.http.put<M>(url, payload, { params, headers });
  }

  public delete<T>(path: string, query?: any): Observable<any> {
    const url = this.combine([this.getBaseUrl(), path]);
    const params = this.prepareQuery(query);
    const headers = this.withHeaders();
    return this.http.delete<T>(url, { params, headers });
  }

  withHeaders(): HttpHeaders {
    const bearer = this.tokenService.getAccessToken();
    return bearer ? this.withAuthBearerHeader(bearer) : this.withApiKeyHeader();
  }

  withAuthBearerHeader(bearer: string): HttpHeaders {
    return new HttpHeaders({
      Accept: '*/*',
      Authorization: 'Bearer ' + bearer,
    });
  }

  withApiKeyHeader(): HttpHeaders {
    return new HttpHeaders({
      Accept: '*/*',
      'X-Api-Key': environment.ceStagingApiKey,
    });
  }

  getBaseUrl() {
    // TODO: Can we encapsulate this into AppService?
    const storeId = this.tokenService.getCurrentStoreId();
    const baseUrl = storeId ? this.appService.getBaseUrl() : this.appService.getTenantBaseUrl();
    return storeId
      ? `${baseUrl.replace('store_id', storeId)}`
      : `${baseUrl.replace('/store_id', '')}`;
  }

  getAccessToken() {
    return this.tokenService.getAccessToken();
  }

  public combine(params: string[]) {
    return params?.length ? params.join('/') : '';
  }

  public prepareQuery(query?: Query): HttpParams {
    const params = removeBlank(query);
    if (params.filters) {
      params.filters = JSON.stringify(this.mapFilters(params.filters));
    }
    if (params.sort_by) {
      params.sort_by = JSON.stringify(params.sort_by);
    } else {
      delete params.sort_by;
    }
    return params as any as HttpParams;
  }

  // TODO: Move this to filters/query

  private mapFilters({ condition, filters }: any) {
    return {
      condition,
      filters: (filters || []).map(this.mapFilter),
    };
  }

  private mapFilter(entry: any) {
    if (entry.type === 'events') {
      const param: any = {
        // type: entry.type,
        event_name: entry.field,
        has_performed: entry.operator === 'performed',
      };

      if (entry.value) {
        const value = base64ToObject(entry.value);
        if (value && typeof value === 'object') param.properties = [value];
      }

      return param;
    }

    return entry;
  }
}
